import { supabase } from '../configs/supabase';

/**
 * Sign in with Google OAuth
 * @returns {Promise<{data: object, error: object}>}
 */
export const signInWithGoogle = async () => {
  try {
    console.log("🟣 Supabase: signInWithGoogle called");
    console.log("🟣 Supabase: Redirect URL will be:", `${window.location.origin}/login`);
    console.log("🟣 Supabase: Calling supabase.auth.signInWithOAuth...");
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });

    console.log("🟣 Supabase: signInWithOAuth response:", { hasData: !!data, error });

    if (error) {
      console.error('❌ Supabase: Error signing in with Google:', error);
      return { data: null, error };
    }

    console.log("🟣 Supabase: OAuth redirect initiated successfully");
    return { data, error: null };
  } catch (error) {
    console.error('❌ Supabase: Exception during Google sign in:', error);
    return { data: null, error };
  }
};

/**
 * Sign out the current user
 * @returns {Promise<{error: object}>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      return { error };
    }
    return { error: null };
  } catch (error) {
    console.error('Exception during sign out:', error);
    return { error };
  }
};

/**
 * Get the current authenticated user
 * @returns {Promise<{user: object, error: object}>}
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('Error getting current user:', error);
      return { user: null, error };
    }
    return { user, error: null };
  } catch (error) {
    console.error('Exception getting current user:', error);
    return { user: null, error };
  }
};

/**
 * Get the current session
 * @returns {Promise<{session: object, error: object}>}
 */
export const getSession = async () => {
  try {
    console.log("🟣 Supabase: getSession called");
    console.log("🟣 Supabase: Calling supabase.auth.getSession()...");
    const { data: { session }, error } = await supabase.auth.getSession();
    console.log("🟣 Supabase: getSession response:", { hasSession: !!session, hasUser: !!session?.user, error });
    if (error) {
      console.error('❌ Supabase: Error getting session:', error);
      return { session: null, error };
    }
    console.log("🟣 Supabase: getSession success");
    return { session, error: null };
  } catch (error) {
    console.error('❌ Supabase: Exception getting session:', error);
    return { session: null, error };
  }
};

/**
 * Store user data in Supabase database
 * This creates or updates a user profile in the 'users' table
 * @param {object} userData - User data from Google OAuth
 * @returns {Promise<{data: object, error: object}>}
 */
export const storeUserData = async (userData) => {
  try {
    if (!userData?.id) {
      return { data: null, error: { message: 'Invalid user data' } };
    }

    // Prepare user profile data
    const profileData = {
      id: userData.id,
      email: userData.email,
      full_name: userData.user_metadata?.full_name || userData.user_metadata?.name || '',
      avatar_url: userData.user_metadata?.avatar_url || userData.user_metadata?.picture || '',
      provider: 'google',
      updated_at: new Date().toISOString(),
    };

    // Check if user already exists
    const { data: existingUser, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userData.id)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 means no rows found, which is fine for new users
      console.error('Error checking existing user:', fetchError);
      return { data: null, error: fetchError };
    }

    let result;
    if (existingUser) {
      // Update existing user
      const { data, error } = await supabase
        .from('users')
        .update(profileData)
        .eq('id', userData.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating user:', error);
        return { data: null, error };
      }
      result = data;
    } else {
      // Insert new user
      profileData.created_at = new Date().toISOString();
      const { data, error } = await supabase
        .from('users')
        .insert(profileData)
        .select()
        .single();

      if (error) {
        console.error('Error creating user:', error);
        return { data: null, error };
      }
      result = data;
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('Exception storing user data:', error);
    return { data: null, error };
  }
};

/**
 * Listen to auth state changes
 * @param {function} callback - Callback function to handle auth state changes
 * @returns {function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
};

/**
 * Delete user account from Supabase
 * This deletes user data from database tables and signs out the user
 * Note: For full auth user deletion, a Supabase Edge Function with admin privileges is required
 * @param {string} userId - User ID to delete
 * @returns {Promise<{data: object, error: object}>}
 */
export const deleteUserAccount = async (userId) => {
  try {
    if (!userId) {
      return { data: null, error: { message: 'User ID is required' } };
    }

    // First, try to call Supabase Edge Function if it exists
    const supabaseUrl = import.meta.env.REACT_APP_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      return { data: null, error: { message: 'No active session' } };
    }

    // Try calling Edge Function first
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        const result = await response.json();
        await signOut();
        return { data: result, error: null };
      }
    } catch (edgeFunctionError) {
      console.log('Edge Function not available, falling back to database deletion');
    }

    // Fallback: Delete user data from database tables
    // Delete from subscriptions (if exists)
    const { error: subError } = await supabase
      .from('subscriptions')
      .delete()
      .eq('user_id', userId);

    if (subError) {
      console.error('Error deleting subscription:', subError);
    }

    // Delete from usage_tracking (if exists)
    const { error: usageError } = await supabase
      .from('usage_tracking')
      .delete()
      .eq('user_id', userId);

    if (usageError) {
      console.error('Error deleting usage tracking:', usageError);
    }

    // Delete from users table
    const { error: userError } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);

    if (userError) {
      console.error('Error deleting user data:', userError);
      return { data: null, error: userError };
    }

    // Sign out the user
    await signOut();

    return { data: { success: true, message: 'Account data deleted successfully' }, error: null };
  } catch (error) {
    console.error('Exception deleting user account:', error);
    return { data: null, error };
  }
};
